import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import dayjs from 'dayjs'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { MAX_SCORE_DAYS, ScoreRecentResponse } from 'features/sentinels/types'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import useSWR, { preload } from 'swr'
import { ChartTimeUnit, PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/** A sentinel the user has added to the chart. */
export interface ActiveSentinel {
  entryId: string
  /** Datapoint entry id of the sentinel's target tag (the pen it bands). */
  target: string
  /** Display name of the sentinel (from its Rolodex entry). */
  name?: string
  /** Chosen method key; undefined = first method in the score response. */
  method?: string
  /** Alerting band width in sigmas; undefined = the method's calibrated k. */
  bandK?: number
  /** False hides the train/test/forecast region tints (monitor contexts). */
  showSplitRegions?: boolean
  /** False hides the out-of-band glow, episode columns, and excursion lane
   * (case charts, where alert markers already pin the excursions). */
  showOutOfBandMarks?: boolean
  /** Training window start (YYYY-MM-DD), from the entry summary. */
  windowStart?: string
  /** Training window end (YYYY-MM-DD) — the test→forecast boundary. */
  windowEnd?: string
  /** HONEST train→test split date from the design fit — overrides the served
   * refit's split (which collapses to the training end). */
  splitDate?: string
}

/** Per-sentinel scoring outcome; failures stay per-entry so one bad sentinel
 * doesn't blank the others. */
export type SentinelScoreResult =
  | { ok: true; response: ScoreRecentResponse }
  | { ok: false; error: string }

export interface SentinelScoreWindow {
  startDate: string
  endDate: string
  /** True when the visible range exceeds the service's scorable span and the
   * band only covers the trailing window. Consumers must surface this. */
  clamped: boolean
}

/**
 * Score the VISIBLE window, capped at MAX_SCORE_DAYS.
 *
 * The chart is meant to explore a sentinel's full data, so the request tracks
 * the visible range (`from` → `to`) rather than a fixed trailing window. When
 * the visible span exceeds the cap we score the trailing cap window and flag
 * `clamped` so consumers can say so, instead of 422-ing on an over-long range.
 *
 * Tradeoff: scoring is window-dependent on the service side (cleaning quantiles
 * are computed over the pulled window, lag/rolling features shift over the
 * cleaned rows), so the band can wobble slightly as the window changes with
 * pan/zoom. That is acceptable for an exploration overlay; the served model's
 * expected is stable to first order and the wobble is second-order.
 *
 * Both request dates are INCLUSIVE: the service adds a day to end_date before
 * checking the span, so the widest accepted window is end_date minus
 * (MAX_SCORE_DAYS - 1).
 */
export function clampScoreWindow(timeRange: {
  from: string
  to: string
}): SentinelScoreWindow {
  const to = dayjs(timeRange.to)
  const from = dayjs(timeRange.from)
  const maxStart = to.subtract(MAX_SCORE_DAYS - 1, 'day')
  const clamped = from.isBefore(maxStart, 'day')
  const start = clamped ? maxStart : from
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: to.format('YYYY-MM-DD'),
    clamped,
  }
}

type ScoresAxios = {
  post: (url: string, body: unknown) => Promise<{ data: unknown }>
}

const sentinelScoresKey = (
  fimServiceUrl: string,
  siteId: string,
  entryIds: string[],
  scoreWindow: SentinelScoreWindow
): [string, string, string, string, string, string] => [
  'sentinel-scores',
  fimServiceUrl,
  siteId,
  entryIds.join(','),
  scoreWindow.startDate,
  scoreWindow.endDate,
]

const fetchSentinelScores = async (
  authorizedAxios: ScoresAxios,
  fimServiceUrl: string,
  siteId: string,
  entryIds: string[],
  scoreWindow: SentinelScoreWindow
): Promise<Record<string, SentinelScoreResult>> => {
  const results = await Promise.all(
    entryIds.map(async (entryId): Promise<[string, SentinelScoreResult]> => {
      try {
        const res = await authorizedAxios.post(
          `${fimServiceUrl}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel/score`,
          {
            entry_id: entryId,
            start_date: scoreWindow.startDate,
            end_date: scoreWindow.endDate,
          }
        )
        const response = res.data as ScoreRecentResponse
        if (!response.ok) {
          return [
            entryId,
            { ok: false, error: response.error ?? t`scoring failed` },
          ]
        }
        return [entryId, { ok: true, response }]
      } catch (err) {
        return [
          entryId,
          {
            ok: false,
            error: err instanceof Error ? err.message : String(err),
          },
        ]
      }
    })
  )
  return Object.fromEntries(results)
}

/**
 * Warm the same SWR cache `useSentinelScores` reads, so a later hook call for
 * this window renders without a network wait. Key and fetcher are shared with
 * the hook, so warmth cannot miss. Opportunistic: failures are swallowed.
 */
export const preloadSentinelScores = (
  authorizedAxios: ScoresAxios,
  fimServiceUrl: string,
  siteId: string,
  entryIds: string[],
  timeRange: { from: string; to: string }
): Promise<void> => {
  if (siteId === '' || entryIds.length === 0) return Promise.resolve()
  const scoreWindow = clampScoreWindow(timeRange)
  return preload(
    sentinelScoresKey(fimServiceUrl, siteId, entryIds, scoreWindow),
    () =>
      fetchSentinelScores(
        authorizedAxios,
        fimServiceUrl,
        siteId,
        entryIds,
        scoreWindow
      )
  ).then(
    () => undefined,
    () => undefined
  )
}

/**
 * Score every active sentinel over the visible range via fim-service
 * (`POST /api/v1/sites/{site}/sentinel/score`). Ephemeral like the Dossiers
 * "Live check" panel: nothing is persisted. SWR keys on (site, sentinels,
 * window) so the sidebar and the chart overlay share one in-flight request.
 */
export const useSentinelScores = (
  siteId: string,
  activeSentinels: ActiveSentinel[],
  timeRange: { from: string; to: string },
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()

  const scoreWindow = clampScoreWindow(timeRange)
  const entryIds = activeSentinels.map((sentinel) => sentinel.entryId)

  // Sentinels are gecko-internal; a restored chart can carry them (and even
  // re-enable the data layer) for anyone it is shared with, so gate the
  // scoring calls here — the one choke point every surface goes through —
  // rather than relying on each UI gate.
  const shouldFetch =
    enabled &&
    isAuthenticated &&
    hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) &&
    siteId !== '' &&
    entryIds.length > 0

  const { data, error, isLoading, isValidating } = useSWR<
    Record<string, SentinelScoreResult>
  >(
    shouldFetch
      ? sentinelScoresKey(FIM_SERVICE_URL, siteId, entryIds, scoreWindow)
      : null,
    () =>
      fetchSentinelScores(
        authorizedAxios,
        FIM_SERVICE_URL,
        siteId,
        entryIds,
        scoreWindow
      ),
    { keepPreviousData: true, revalidateOnFocus: false }
  )

  return {
    scores: data ?? {},
    scoreWindow,
    error,
    isLoading,
    isValidating,
  }
}

/** One sentinel's alerting range, ready for the uPlot band plugin. */
export interface SentinelBandOverlay {
  /**
   * Holes between scored buckets narrower than this are bridged, the way the
   * pens bridge them; wider ones break the band. Unset means the score
   * cadence decides (1.5 buckets on a grid, the strided limit otherwise).
   */
  gapToleranceMs?: number
  targetSensorId: string
  /** Display name of the sentinel driving this overlay. */
  sentinelName: string
  method: string
  label: string
  color: string
  /** Epoch-ms x positions, ascending (score cadence, e.g. 10 min). */
  xs: number[]
  upper: (number | null)[]
  lower: (number | null)[]
  /** The model's expected value — the band's center line. */
  center: (number | null)[]
  /** The scored actual target value per bucket (where the marks land). */
  actual: (number | null)[]
  /**
   * residual / err_scale per bucket — how many sigmas out the actual is,
   * SIGNED: positive above the expectation, negative below.
   */
  sigma: (number | null)[]
  /**
   * Epoch-ms of the train→test split (from `trained_split_date`). Drawn as a
   * marker so the chart shows where the held-out test window began at design
   * time. Null when the model did not record one.
   */
  trainedSplitMs: number | null
  /**
   * Epoch-ms of the end of the trained window (from `model_created_at`) — the
   * test→future boundary, past which the band is forward scoring the served
   * model on data it never trained on. Null when unknown.
   */
  modelCreatedMs: number | null
  /**
   * Epoch-ms of the training window START. The split regions anchor to this
   * (the model's own window), NOT to where the scored series happens to end —
   * historian data can stop before the split date, which would otherwise
   * collapse the test/forecast regions. Null when unknown.
   */
  windowStartMs: number | null
  /**
   * Resolved train/val/test windows as epoch-ms spans, when the source knows
   * them at that granularity (a design/experiment fit's per-point roles). The
   * chart shades each span so a multi-window custom split reads as its regions,
   * not just a single boundary line. Absent for a served sentinel scored via
   * `/score`, which only knows the two scalar boundaries above — the chart
   * derives train/test/future from those instead.
   */
  splitRanges?: Array<{ startMs: number; endMs: number; role: string }>
  /**
   * The pen that ALREADY draws this overlay's expected value, when one exists.
   *
   * A sentinel's expectation lives only in the overlay, so the chart strokes the
   * band's center line and the readout adds a "(predicted)" row. A gray-box
   * model instead saves its expectation as a real pen, which draws its own line
   * and carries its own readout row, y-axis and colour — so doing both puts the
   * same number on screen twice under two names. Set this and the overlay yields
   * the center to the pen: band and sigma, no duplicate line, no duplicate row.
   */
  centerPenId?: string | null
  /** Out-of-band warn threshold in sigmas; undefined = SIGMA_WARN. */
  warnSigma?: number
  /** False suppresses split-region tints and labels for this overlay. */
  showSplitRegions?: boolean
  /** False suppresses the out-of-band glow, columns, and excursion lane. */
  showOutOfBandMarks?: boolean
}

/**
 * Chart tints per split-region role, keyed to the boundary-marker colors so a
 * region and the line that opens it read as one thing. `train` is deliberately
 * absent: it is the baseline (untinted), so the chart highlights only the
 * held-out `test` window and the `forecast` span where the served model is
 * extrapolating on data it never trained on — the regions worth noticing.
 * Alpha is low enough to sit behind busy multi-pen data but high enough to see.
 */
export const SPLIT_ROLE_FILL: Record<string, string> = {
  val: 'rgba(249, 115, 22, 0.10)',
  test: 'rgba(148, 163, 184, 0.12)',
  future: 'rgba(245, 158, 11, 0.10)',
}

/** Sigma level at which a point counts as out of band on the chart. */
export const SIGMA_WARN = 2
/**
 * The one color for every out-of-band affordance (glow, lane, columns,
 * tooltip callout) — matches the thresholds layer blue. Deliberately not an
 * alarm palette.
 */
export const SENTINEL_OUT_OF_BAND_COLOR = '#60a5fa'

/** Score cadence assumed when a response has too few buckets to measure. */
export const DEFAULT_SCORE_CADENCE_MS = 10 * 60 * 1000

const ONE_DAY_MS = 24 * 60 * 60 * 1000

/**
 * The chart time range that frames a model's train+test window (plus a forecast
 * tail so post-training data is visible), so adding a sentinel jumps the chart
 * to where its band and split boundaries actually are. Returns null when the
 * window is missing or invalid (leave the range as-is).
 */
export function sentinelWindowRange(
  windowStart: string | undefined,
  windowEnd: string | undefined,
  penCount: number
): {
  from: string
  to: string
  preset: 'custom'
  interval: number
  intervalUnit: ChartTimeUnit
} | null {
  if (!windowStart || !windowEnd) return null
  const from = dayjs(windowStart)
  const trainEnd = dayjs(windowEnd).endOf('day')
  if (!from.isValid() || !trainEnd.isValid() || !trainEnd.isAfter(from)) {
    return null
  }
  // A forecast tail of 15% of the window (at least a week) so the "forecast"
  // region past the training end is on screen.
  const tailMs = Math.max(trainEnd.diff(from) * 0.15, 7 * ONE_DAY_MS)
  // Never past now: a window that ends next year reads as data that exists
  // next year, to the eye and to the agent that is handed the visible range.
  const now = dayjs()
  const tailEnd = trainEnd.add(tailMs, 'millisecond')
  const to = tailEnd.isAfter(now) && now.isAfter(from) ? now : tailEnd
  const { interval, interval_unit } = TimeSeriesService.getIntervalForPenCount(
    to.diff(from, 'second'),
    Math.max(1, penCount)
  )
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    preset: 'custom',
    interval,
    intervalUnit: interval_unit,
  }
}

/**
 * The score cadence measured from the buckets themselves: the smallest
 * positive spacing. The service re-applies the sentinel's cleaning plan when
 * scoring, and gate ops DROP rows, so `xs` is NOT uniformly spaced — any
 * single pair (in particular `xs[1] - xs[0]`) can straddle a multi-hour gap.
 * The minimum spacing recovers the underlying interval_minutes because any
 * two consecutive surviving rows sit one interval apart.
 */
export function scoreCadenceMs(xs: number[]): number {
  let smallest = Infinity
  for (let i = 1; i < xs.length; i++) {
    const delta = xs[i] - xs[i - 1]
    if (delta > 0 && delta < smallest) smallest = delta
  }
  return Number.isFinite(smallest) ? smallest : DEFAULT_SCORE_CADENCE_MS
}

/** How much wider than the expected step a hole has to be to count as one. */
export const SCORE_GAP_FACTOR = 1.5

/** Steps needed before a step quantile means anything (see below). */
const MIN_STEPS_FOR_QUANTILE = 50

/**
 * The widest step that still reads as "the next bucket", so a band run only
 * breaks at a genuine hole.
 *
 * On a uniform grid the minimum spacing IS the interval, so `cadence * factor`
 * is the whole answer. A STRIDED series is different: the service caps the
 * train series at a point budget, which thins a long window's history far
 * harder than its recent span, so consecutive buckets sit a stride apart and
 * the stride varies across the window. There the minimum spacing is a small
 * fraction of a typical step, and keying contiguity off it reads almost every
 * step as a hole.
 *
 * The 90th-percentile step absorbs that thinning while still isolating real
 * holes, and the cadence floor means a clean grid is unaffected (its p90 is
 * the interval once under a tenth of the rows are missing).
 */
export function scoreContiguityLimitMs(xs: number[]): number {
  const floor = scoreCadenceMs(xs) * SCORE_GAP_FACTOR

  const steps: number[] = []
  for (let i = 1; i < xs.length; i++) {
    const delta = xs[i] - xs[i - 1]
    if (delta > 0) steps.push(delta)
  }
  // Across a handful of steps a single hole IS the 90th percentile, so the
  // quantile says nothing and the cadence is the safer read. Strided series
  // are thousands of buckets long, so this only skips short ones.
  if (steps.length < MIN_STEPS_FOR_QUANTILE) return floor

  steps.sort((left, right) => left - right)
  const p90 = steps[Math.min(steps.length - 1, Math.floor(steps.length * 0.9))]
  return Math.max(floor, p90 * SCORE_GAP_FACTOR)
}

/**
 * Index of the bucket whose timestamp is closest to `timestampMs` (no
 * distance limit — callers apply their own tolerance). Binary search over the
 * ascending `xs`, so per-sample matching in the draw hooks stays cheap.
 */
export function nearestBucketIndex(
  xs: number[],
  timestampMs: number
): number | null {
  if (xs.length === 0) return null
  let lo = 0
  let hi = xs.length - 1
  if (timestampMs <= xs[0]) return 0
  if (timestampMs >= xs[hi]) return hi
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (xs[mid] <= timestampMs) lo = mid
    else hi = mid
  }
  return timestampMs - xs[lo] <= xs[hi] - timestampMs ? lo : hi
}

/**
 * Index of the score bucket nearest a chart timestamp, within ~a bucket's
 * width, so lookups tolerate the chart cadence differing from the score
 * cadence. Timestamps that fall inside a cleaning gap (no bucket within
 * three-quarters of the cadence) answer null rather than snapping to a
 * far-away bucket.
 */
export function bucketIndexAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs'>,
  timestampMs: number
): number | null {
  const { xs } = overlay
  const index = nearestBucketIndex(xs, timestampMs)
  if (index === null) return null
  const cadence = scoreCadenceMs(xs)
  return Math.abs(xs[index] - timestampMs) > cadence * 0.75 ? null : index
}

/** Signed sigmas-out at (or near) a chart timestamp. */
export function sigmaAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs' | 'sigma'>,
  timestampMs: number
): number | null {
  const index = bucketIndexAtTime(overlay, timestampMs)
  return index === null ? null : (overlay.sigma[index] ?? null)
}

/**
 * Band edges at the cursor when there is one, else at the latest scored bucket.
 * Follows the same rule as `sentinelReadingFor`.
 */
export function bandEdgesFor(
  overlay: SentinelBandOverlay | undefined,
  xValue: string | number | null | undefined
): { upper: number; lower: number } | null {
  if (!overlay) return null

  const timestampMs =
    typeof xValue === 'number'
      ? xValue
      : typeof xValue === 'string'
        ? new Date(xValue).getTime()
        : null

  if (timestampMs !== null && Number.isFinite(timestampMs)) {
    const edges = bandEdgesAtTime(overlay, timestampMs)
    if (edges) return edges
  }

  for (let i = overlay.xs.length - 1; i >= 0; i--) {
    const upper = overlay.upper[i]
    const lower = overlay.lower[i]
    if (typeof upper === 'number' && typeof lower === 'number') {
      return { upper, lower }
    }
  }
  return null
}

/** Upper and lower band values at (or near) a chart timestamp. */
export function bandEdgesAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs' | 'upper' | 'lower'>,
  timestampMs: number
): { upper: number; lower: number } | null {
  const index = bucketIndexAtTime(overlay, timestampMs)
  if (index === null) return null

  const upper = overlay.upper[index]
  const lower = overlay.lower[index]
  return typeof upper === 'number' && typeof lower === 'number'
    ? { upper, lower }
    : null
}

/** The model's expected value at (or near) a chart timestamp. */
export function expectedAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs' | 'center'>,
  timestampMs: number
): number | null {
  const index = bucketIndexAtTime(overlay, timestampMs)
  return index === null ? null : (overlay.center[index] ?? null)
}

/**
 * The sentinel's prediction and signed sigma for a legend/drawer row: at the
 * row's displayed timestamp (cursor-following) when available, else the
 * latest scored bucket.
 */
export function sentinelReadingFor(
  overlay: SentinelBandOverlay | undefined,
  xValue: string | number | null | undefined
): { predicted: number; sigma: number } | null {
  // An overlay whose expectation is already a pen has no prediction to add: the
  // pen's own row carries that number, under the pen's own name.
  if (!overlay || overlay.centerPenId) return null
  const timestampMs =
    typeof xValue === 'number'
      ? xValue
      : typeof xValue === 'string'
        ? new Date(xValue).getTime()
        : null
  if (timestampMs !== null && Number.isFinite(timestampMs)) {
    const predicted = expectedAtTime(overlay, timestampMs)
    const sigma = sigmaAtTime(overlay, timestampMs)
    if (predicted !== null && sigma !== null) return { predicted, sigma }
  }
  const latest = latestOverlayReading(overlay)
  return latest ? { predicted: latest.expected, sigma: latest.sigma } : null
}

/**
 * Signed residual (actual − expected) matching sentinelReadingFor's bucket
 * choice: the cursor's bucket when given, else the latest scored one.
 */
export function residualFor(
  overlay: SentinelBandOverlay | undefined,
  xValue: string | number | null | undefined
): number | null {
  if (!overlay) return null
  const timestampMs =
    typeof xValue === 'number'
      ? xValue
      : typeof xValue === 'string'
        ? new Date(xValue).getTime()
        : null
  const residualAt = (index: number): number | null => {
    const actual = overlay.actual[index]
    const expected = overlay.center[index]
    return typeof actual === 'number' && typeof expected === 'number'
      ? actual - expected
      : null
  }
  if (timestampMs !== null && Number.isFinite(timestampMs)) {
    const index = bucketIndexAtTime(overlay, timestampMs)
    if (index !== null) {
      const residual = residualAt(index)
      if (residual !== null) return residual
    }
  }
  for (let i = overlay.center.length - 1; i >= 0; i--) {
    const residual = residualAt(i)
    if (residual !== null) return residual
  }
  return null
}

/**
 * The most recent bucket with both an expected value and a sigma — the
 * sentinel's live status readout (used by the tag-names sidebar).
 */
export function latestOverlayReading(
  overlay: Pick<SentinelBandOverlay, 'center' | 'sigma'>
): { expected: number; sigma: number } | null {
  for (let i = overlay.center.length - 1; i >= 0; i--) {
    const expected = overlay.center[i]
    const sigma = overlay.sigma[i]
    if (
      expected !== null &&
      expected !== undefined &&
      sigma !== null &&
      sigma !== undefined
    ) {
      return { expected, sigma }
    }
  }
  return null
}

/**
 * Turn score responses into drawable band edges: `expected ± residual_band`
 * for each sentinel's chosen method (defaulting to the first method the
 * service returned). Colored like the target pen so the association is
 * obvious on the chart.
 */
export function buildSentinelBandOverlays({
  activeSentinels,
  scores,
  allSensorsToRender,
  customSensorColors,
}: {
  activeSentinels: ActiveSentinel[]
  scores: Record<string, SentinelScoreResult>
  allSensorsToRender: Array<{ id: string; name: string }>
  customSensorColors?: Record<string, string>
}): SentinelBandOverlay[] {
  const overlays: SentinelBandOverlay[] = []

  for (const sentinel of activeSentinels) {
    const score = scores[sentinel.entryId]
    if (!score?.ok) continue
    const { response } = score
    const methods = response.methods ?? {}
    const methodKey =
      sentinel.method && methods[sentinel.method]
        ? sentinel.method
        : Object.keys(methods)[0]
    if (!methodKey) continue
    const method = methods[methodKey]

    const targetKey = response.target ?? sentinel.target
    const expected = method.expected[targetKey]
    const calibratedBand = method.residual_band[targetKey]
    if (!expected || calibratedBand === null || calibratedBand === undefined)
      continue
    const kScale =
      sentinel.bandK && method.band_k ? sentinel.bandK / method.band_k : 1
    const band = calibratedBand * kScale

    const xs = (response.ts ?? []).map((ts) => new Date(ts).getTime())
    if (xs.length === 0 || xs.length !== expected.length) continue

    const penIndex = allSensorsToRender.findIndex(
      (sensor) => sensor.id === sentinel.target
    )
    const color =
      customSensorColors?.[sentinel.target] ??
      getSensorColor(penIndex >= 0 ? penIndex : 0)

    const residual = method.residual[targetKey]
    const errScale = method.err_scale[targetKey]
    const actual = response.actual?.[targetKey]
    const sigma = xs.map((_, i) => {
      const r = residual?.[i]
      if (r === null || r === undefined || !errScale) return null
      return r / errScale
    })

    const toMs = (value?: string): number | null => {
      if (!value) return null
      const ms = new Date(value).getTime()
      return Number.isFinite(ms) ? ms : null
    }

    // The train→test boundary. The served /score reports the REFIT split,
    // which is the training end (no honest holdout) — so prefer the design
    // fit's honest split date carried on the sentinel (from the entry summary).
    // The test→forecast boundary is the training end (`windowEnd`), else the
    // served model's creation time.
    const modelCreatedMs =
      toMs(sentinel.windowEnd) ?? toMs(response.model_created_at)
    const honestSplitMs = toMs(sentinel.splitDate)
    const rawSplitMs = honestSplitMs ?? toMs(response.trained_split_date)
    // Only null out a split that coincides with the forecast boundary when we
    // had to fall back to the refit's (no honest split available).
    const trainedSplitMs =
      honestSplitMs === null &&
      rawSplitMs !== null &&
      modelCreatedMs !== null &&
      Math.abs(rawSplitMs - modelCreatedMs) < ONE_DAY_MS
        ? null
        : rawSplitMs

    overlays.push({
      targetSensorId: sentinel.target,
      sentinelName: sentinel.name ?? sentinel.entryId,
      method: methodKey,
      label: `${targetKey} ±${sentinel.bandK ?? method.band_k}σ`,
      color,
      xs,
      upper: expected.map((value) => (value === null ? null : value + band)),
      lower: expected.map((value) => (value === null ? null : value - band)),
      center: expected,
      actual: actual ?? xs.map(() => null),
      sigma,
      trainedSplitMs,
      modelCreatedMs,
      windowStartMs: toMs(sentinel.windowStart),
      warnSigma: sentinel.bandK,
      showSplitRegions: sentinel.showSplitRegions,
      showOutOfBandMarks: sentinel.showOutOfBandMarks,
    })
  }

  return overlays
}

/**
 * Cheap order-sensitive checksum over an overlay's drawable values, for the
 * chart-recreation signature. Endpoint + count digests alone can miss a
 * response whose interior values changed (same surviving buckets, different
 * expected/band) — the retained uPlot options would then keep drawing the
 * previous window's band through a stale plugin closure.
 */
export function sentinelOverlayValuesDigest(
  overlay: Pick<
    SentinelBandOverlay,
    'xs' | 'upper' | 'lower' | 'center' | 'actual' | 'sigma'
  >
): number {
  const bits = new DataView(new ArrayBuffer(8))
  let hash = 0
  const mix = (value: number | null | undefined) => {
    // Nulls perturb the hash too, so a value appearing or disappearing at
    // the same index still changes the digest.
    bits.setFloat64(0, value === null || value === undefined ? NaN : value)
    hash = Math.imul(hash ^ bits.getInt32(0), 2246822519)
    hash = Math.imul(hash ^ bits.getInt32(4), 2654435761)
  }
  for (let i = 0; i < overlay.xs.length; i++) {
    mix(overlay.xs[i])
    mix(overlay.upper[i])
    mix(overlay.lower[i])
    mix(overlay.center[i])
    mix(overlay.actual[i])
    mix(overlay.sigma[i])
  }
  return hash
}
